import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faFutbol } from '@fortawesome/pro-regular-svg-icons';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelL } from '@sorare/core/src/atoms/typography';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useDefaultStadiumBackground } from '@sorare/core/src/hooks/useDefaultStadiumBackground';

import { NeonRewardConfigs } from 'components/rewards/NeonRewards/NeonRewardConfigs';

import type {
  CurrentFixture_so5Fixture,
  CurrentFixture_user,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  background:
    linear-gradient(to right, var(--c-nl-700), var(--c-nl-700)),
    no-repeat center / cover var(--bg);
`;

type Props = {
  so5Fixture: CurrentFixture_so5Fixture | null;
  sport: Sport;
  user: CurrentFixture_user | null;
};
export const CurrentFixture = ({ so5Fixture, sport, user }: Props) => {
  const defaultStadiumBackground = useDefaultStadiumBackground(sport);
  const { generateSportContextPath } = useSportContext();

  if (!so5Fixture || !user) {
    return null;
  }

  const { totalRewardConfigs, so5LeaderboardContenders } =
    so5Fixture.userFixtureResults || {};

  const title = (() => {
    if (totalRewardConfigs?.length === 0) {
      return (
        <FormattedMessage
          id="CurrentFixture.noReward"
          defaultMessage="No reward"
        />
      );
    }
    if (so5LeaderboardContenders?.totalCount === 0) {
      return (
        <FormattedMessage
          id="CurrentFixture.noLineup"
          defaultMessage="No lineup set"
        />
      );
    }
    return (
      <FormattedMessage id="CurrentFixture.rewards" defaultMessage="Rewards" />
    );
  })();

  const redirectToPreviousFixture = !totalRewardConfigs?.length;

  const profiles: Record<
    Sport,
    | CurrentFixture_user['footballUserProfile']
    | CurrentFixture_user['nbaUserProfile']
    | CurrentFixture_user['baseballUserProfile']
  > = {
    [Sport.FOOTBALL]: user.footballUserProfile,
    [Sport.NBA]: user.nbaUserProfile,
    [Sport.BASEBALL]: user.baseballUserProfile,
  };

  const fixtureToRedirectTo =
    redirectToPreviousFixture && so5Fixture.previous
      ? so5Fixture.previous
      : so5Fixture;

  return (
    <Root
      style={
        {
          '--bg': `url(${profiles[sport]?.background?.pictureUrl || defaultStadiumBackground})`,
        } as CSSProperties
      }
    >
      <Vertical gap={2}>
        <Horizontal>
          <FontAwesomeIcon icon={faFutbol} />
          <LabelL bold>
            <FormattedMessage
              id="CurrentFixture.title"
              defaultMessage="Current GW"
            />
          </LabelL>
        </Horizontal>
        <Vertical center>
          <Vertical center gap={3}>
            <Avatar user={user} rounded size={6} />
            <BrandXS color="var(--c-nd-600)">{title}</BrandXS>
          </Vertical>
          <NeonRewardConfigs
            rewardConfigs={totalRewardConfigs || []}
            size={16}
            iconSize={2}
            displayLabels={false}
          />
          <Button
            to={generateSportContextPath(
              ANY_SPORT_MY_CIRCLES_EVENTTYPE_FIXTURE_SLUG,
              {
                params: {
                  slug: user.slug,
                  fixture: fixtureToRedirectTo.slug,
                  eventType: fixtureToRedirectTo.type.toLowerCase(),
                },
              }
            )}
            color="quaternary"
            size="medium"
          >
            {redirectToPreviousFixture ? (
              <FormattedMessage
                id="CurrentFixture.cta.text.previousFixture"
                defaultMessage="View Previous GW"
              />
            ) : (
              <FormattedMessage
                id="CurrentFixture.cta.text"
                defaultMessage="View GW"
              />
            )}
          </Button>
        </Vertical>
      </Vertical>
    </Root>
  );
};

CurrentFixture.fragments = {
  so5Fixture: gql`
    fragment CurrentFixture_so5Fixture on So5Fixture {
      slug
      type
      previous {
        slug
        type
      }
      userFixtureResults(userSlug: $user) {
        slug
        so5LeaderboardContenders {
          totalCount
        }
        totalRewardConfigs {
          id
          ...NeonRewardConfigs_rewardConfig
        }
      }
    }
    ${NeonRewardConfigs.fragments.rewardConfig}
  ` as TypedDocumentNode<CurrentFixture_so5Fixture>,
  user: gql`
    fragment CurrentFixture_user on User {
      slug
      id
      footballUserProfile {
        id
        background {
          id
          pictureUrl
        }
      }
      baseballUserProfile {
        id
        background {
          id
          pictureUrl
        }
      }
      nbaUserProfile {
        id
        background {
          id
          pictureUrl
        }
      }
      ...Avatar_user
    }
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<CurrentFixture_user>,
};
